import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VsPageLayout } from "./VsPageLayout";

const K = "polls:ai_page.";
const MCP_URL = "https://doodates.com/mcp";
const EXAMPLE_LINK =
  "https://doodates.com/new?title=D%C3%AEner%20entre%20amis&dates=2027-01-15,2027-01-16&slots=20:00-23:00";

const CONNECT_STEPS = ["connect_step1", "connect_step2", "connect_step3"];
const EXAMPLES = ["example_dinner", "example_team", "example_ag"];
const CAN = ["can_create", "can_links", "can_results", "can_close"];
const FORMAT = [
  "format_title_param",
  "format_dates_param",
  "format_slots_param",
  "format_times_param",
];
const PROMISES = [
  "promise_account",
  "promise_ads",
  "promise_participants",
  "promise_eu",
];

/** Page /ai : DooDates depuis un assistant IA (connecteur MCP ou lien /new). */
export default function AiPage() {
  const { t, i18n } = useTranslation(["polls"]);
  const k = (key: string) => t(`${K}${key}`);
  const section =
    "mb-12 rounded-2xl border border-border bg-muted/30 p-4 sm:p-8";
  const h2 = "text-2xl font-bold mb-4 text-foreground";

  return (
    <VsPageLayout
      seo={{
        title: k("meta_title"),
        description: k("meta_description"),
        fullTitle: true,
        // Spec §3.5 point 4 : l'application et la procédure de connexion,
        // dans le HTML pré-rendu (remplace le WebPage générique de SEOHead).
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "SoftwareApplication",
              name: "DooDates",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: "https://doodates.com/ai",
              description: k("meta_description"),
              inLanguage: i18n.language,
              offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
            },
            {
              "@type": "HowTo",
              name: k("connect_title"),
              inLanguage: i18n.language,
              step: CONNECT_STEPS.map((s, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                text: k(s),
              })),
            },
          ],
        },
      }}
      heading={k("heading")}
      intro={k("intro")}
    >
      <section className={section} data-testid="ai-connect">
        <h2 className={h2}>{k("connect_title")}</h2>
        <ol className="list-decimal pl-5 space-y-2">
          {CONNECT_STEPS.map((s) => (
            <li key={s}>{k(s)}</li>
          ))}
        </ol>
        <p className="mt-4 text-sm">
          <span className="font-semibold">{k("mcp_url_label")}</span>{" "}
          <code className="break-all rounded bg-background px-2 py-1">
            {MCP_URL}
          </code>
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          {k("connect_note")}
        </p>
      </section>

      <section className={section}>
        <h2 className={h2}>{k("examples_title")}</h2>
        <ul className="space-y-3">
          {EXAMPLES.map((e) => (
            <li key={e} className="rounded-xl bg-background p-4 italic">
              {k(e)}
            </li>
          ))}
        </ul>
      </section>

      <section className={section}>
        <h2 className={h2}>{k("can_title")}</h2>
        <ul className="list-disc pl-5 space-y-2">
          {CAN.map((c) => (
            <li key={c}>{k(c)}</li>
          ))}
        </ul>
      </section>

      <section className={section} data-testid="ai-no-connector">
        <h2 className={h2}>{k("nolink_title")}</h2>
        <p className="mb-4">{k("nolink_intro")}</p>
        <pre className="whitespace-pre-wrap break-words rounded-xl bg-background p-4 text-sm">
          {k("nolink_instruction")}
        </pre>
        <h3 className="mt-6 mb-2 text-lg font-semibold">{k("format_title")}</h3>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          {FORMAT.map((f) => (
            <li key={f}>{k(f)}</li>
          ))}
        </ul>
        <p className="mt-4 text-sm">
          <span className="font-semibold">{k("format_example_label")}</span>{" "}
          <a className="break-all text-primary underline" href={EXAMPLE_LINK}>
            {EXAMPLE_LINK}
          </a>
        </p>
      </section>

      <section className={section}>
        <h2 className={h2}>{k("promise_title")}</h2>
        <ul className="list-disc pl-5 space-y-2">
          {PROMISES.map((p) => (
            <li key={p}>{k(p)}</li>
          ))}
        </ul>
      </section>

      <div className="text-center">
        <Link
          to="/create"
          className="inline-flex rounded-xl bg-primary px-8 py-3 font-medium text-primary-foreground hover:bg-primary/90"
        >
          {k("cta")}
        </Link>
      </div>
    </VsPageLayout>
  );
}
